import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Search, MapPin, User, Loader2, Navigation, Palette } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useGeoLocation } from "@/contexts/LocationContext";
import { useSearch } from "@/hooks/useSearch";
import { useI18n } from "@/contexts/I18nContext";
import SeedRequestForm from "@/components/SeedRequestForm";

interface SmartSearchProps {
  placeholder?: string;
  className?: string;
  showNearMe?: boolean;
  autoFocus?: boolean;
  size?: "sm" | "md" | "lg";
  /** Compact inline mode for header usage */
  inline?: boolean;
  /** When provided, Near Me triggers this callback instead of navigating */
  onNearMe?: () => void;
}

export default function SmartSearch({
  placeholder,
  className,
  showNearMe = true,
  size = "md",
  inline = false,
  onNearMe,
}: SmartSearchProps) {
  const navigate = useNavigate();
  const userLocation = useGeoLocation();
  const { t } = useI18n();
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const resolvedPlaceholder =
    placeholder || (t("smart_search.placeholder" as any) || "Zoek een stad of tattoo artist...");

  const {
    cities: cityResults,
    artists: artistResults,
    studios: studioResults,
    styles: styleResults,
    isLoading,
    hasResults,
    noResults,
  } = useSearch({ query });

  useEffect(() => {
    setOpen(query.length >= 2);
  }, [query]);

  useEffect(() => {
    function handler(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const goToCity = (citySlug: string, country: string) => {
    navigate(`/tattoo/${country.toLowerCase()}/${citySlug}`);
    setOpen(false);
    setQuery("");
  };

  const goToArtist = (slug: string) => {
    navigate(`/c/${slug}`);
    setOpen(false);
    setQuery("");
  };

  const goToStudio = (studio: any) => {
    const countrySlug = (studio.country || "NL").toLowerCase();
    navigate(`/tattoo/${countrySlug}/${studio.city_slug}/${studio.slug}`);
    setOpen(false);
    setQuery("");
  };

  const goToStyle = (styleId: string) => {
    navigate(`/tattoo/stijl/${styleId.replace(/_/g, "-")}`);
    setOpen(false);
    setQuery("");
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key !== "Enter") return;
    const q = query.trim();
    if (!q || q.length < 2) return;
    const exactCity = cityResults.find((c) => c.city.toLowerCase() === q.toLowerCase());
    if (exactCity) {
      goToCity(exactCity.city_slug, exactCity.country);
      return;
    }
    if (cityResults.length > 0) {
      goToCity(cityResults[0].city_slug, cityResults[0].country);
      return;
    }
    if (artistResults.length > 0 && artistResults[0].slug) {
      goToArtist(artistResults[0].slug);
      return;
    }
    navigate(`/tattoo?search=${encodeURIComponent(q)}`);
    setOpen(false);
  };

  const handleNearMe = () => {
    if (onNearMe) {
      onNearMe();
      return;
    }
    if (!userLocation.loading && userLocation.detectedCitySlug) {
      navigate(`/tattoo/${userLocation.countrySlug}/${userLocation.detectedCitySlug}`);
    } else if (!userLocation.loading) {
      navigate(`/tattoo/${userLocation.countrySlug}`);
    }
  };

  const inputHeight = inline
    ? "h-9 text-sm"
    : size === "lg"
      ? "h-13 text-base"
      : size === "sm"
        ? "h-9 text-sm"
        : "h-11";

  return (
    <div ref={containerRef} className={cn("relative", className)}>
      <div className="flex gap-1.5">
        <div className="relative flex-1">
          {isLoading ? (
            <Loader2 className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground animate-spin" />
          ) : (
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          )}
          <Input
            ref={inputRef}
            data-testid="smart-search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            onFocus={() => query.length >= 2 && setOpen(true)}
            placeholder={resolvedPlaceholder}
            className={cn("pl-10 bg-card border-border", inputHeight)}
          />
        </div>

        {/* Near Me icon button */}
        {showNearMe && (
          <Button
            variant="outline"
            size="icon"
            onClick={handleNearMe}
            className={cn(
              "shrink-0 text-muted-foreground hover:text-primary hover:border-primary/40",
              inline ? "w-9 h-9" : "w-11 h-11",
              size === "lg" && !inline && "w-13 h-13",
            )}
            title={t("discover.near_me" as any) || "In de buurt"}
          >
            <Navigation className="h-4 w-4" />
          </Button>
        )}
      </div>

      {/* Dropdown */}
      {open && (
        <div
          className={cn(
            "absolute z-50 top-full left-0 right-0 mt-1 rounded-xl border border-border bg-card shadow-lg max-h-[400px] overflow-y-auto",
            inline && "min-w-[320px]",
          )}
        >
          {noResults ? (
            <div className="p-4">
              <p className="text-sm text-muted-foreground mb-3">
                {t("smart_search.no_results" as any) || "Geen resultaten voor"} "{query}". {t("smart_search.request_city" as any) || "Wil je deze stad aanvragen?"}
              </p>
              <SeedRequestForm prefillCity={query} compact />
            </div>
          ) : (
            <>
              {/* Styles */}
              {styleResults.length > 0 && (
                <div className="py-2">
                  <p className="px-3 py-1 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
                    {t("smart_search.styles" as any) || "Stijlen"}
                  </p>
                  {styleResults.map((style) => (
                    <button
                      key={style.id}
                      onClick={() => goToStyle(style.id)}
                      className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-muted/40 transition-colors text-left"
                    >
                      <Palette className="h-4 w-4 text-primary shrink-0" />
                      <p className="text-sm font-medium text-foreground capitalize">{style.label}</p>
                    </button>
                  ))}
                </div>
              )}

              {/* Cities */}
              {cityResults.length > 0 && (
                <div className={cn("py-2", styleResults.length > 0 ? "border-t border-border/50" : "")}>
                  <p className="px-3 py-1 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
                    {t("smart_search.cities" as any) || "Steden"}
                  </p>
                  {cityResults.map((city) => (
                    <button
                      key={city.city_slug}
                      onClick={() => goToCity(city.city_slug, city.country)}
                      className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-muted/40 transition-colors text-left"
                    >
                      <MapPin className="h-4 w-4 text-primary shrink-0" />
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-foreground">{city.city}</p>
                        <p className="text-xs text-muted-foreground">
                          {city.studio_count} {t("nav.studios_count" as any) || "studio's"} · {city.country}
                        </p>
                      </div>
                    </button>
                  ))}
                </div>
              )}

              {/* Artists */}
              {artistResults.length > 0 && (
                <div
                  className={cn(
                    "py-2",
                    cityResults.length > 0 || styleResults.length > 0 ? "border-t border-border/50" : "",
                  )}
                >
                  <p className="px-3 py-1 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
                    {t("smart_search.artists" as any) || "Artiesten"}
                  </p>
                  {artistResults.map((artist) => (
                    <button
                      key={artist.id}
                      onClick={() => artist.slug && goToArtist(artist.slug)}
                      className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-muted/40 transition-colors text-left"
                    >
                      <User className="h-4 w-4 text-primary shrink-0" />
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-foreground flex items-center gap-1.5">
                          {artist.artist_name}
                          {artist.is_verified && <span className="text-primary text-xs">✓</span>}
                        </p>
                        {artist.studio_name && (
                          <p className="text-xs text-muted-foreground">
                            {artist.studio_name} · {artist.city}
                          </p>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              )}

              {/* Studios */}
              {studioResults.length > 0 && (
                <div
                  className={cn(
                    "py-2",
                    cityResults.length > 0 || artistResults.length > 0 || styleResults.length > 0
                      ? "border-t border-border/50"
                      : "",
                  )}
                >
                  <p className="px-3 py-1 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
                    {t("smart_search.studios" as any) || "Studio's"}
                  </p>
                  {studioResults.map((studio) => (
                    <button
                      key={studio.id}
                      onClick={() => goToStudio(studio)}
                      className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-muted/40 transition-colors text-left"
                    >
                      <MapPin className="h-4 w-4 text-muted-foreground shrink-0" />
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-foreground">{studio.name}</p>
                        <p className="text-xs text-muted-foreground">
                          {studio.city} · {t("smart_search.unclaimed" as any) || "Ongeclaimd"}
                        </p>
                      </div>
                    </button>
                  ))}
                </div>
              )}

              {!hasResults && !isLoading && query.length >= 2 && (
                <p className="px-4 py-6 text-sm text-muted-foreground text-center">
                  {t("smart_search.searching" as any) || "Zoeken..."}
                </p>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
